import React, { useState, useEffect } from 'react';
import BBALoader from '../components/BBALoader';
import { useNavigate } from 'react-router-dom';
import { FileText, FileCheck, ChevronLeft, ChevronRight, Lock } from 'lucide-react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { getAccountById } from '../services/firestoreService';
import BackButton from '../components/BackButton';
import StartSidebar from '../components/StartSidebar';

const ALL_TAB_IDS = ['proposal', 'contracts', 'buildProposal', 'buildContract'];
/** Must match `freePage` on the account and START_PAGES value for Proposals */
const PROPOSALS_FREE_PAGE = 'sales-proposals';

const StartProposals = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  /** undefined = still loading; string | null after load */
  const [accountFreePage, setAccountFreePage] = useState(undefined);
  const [unlockedTabIds, setUnlockedTabIds] = useState(null); // null = not loaded, [] or array = from account (empty = show all for backward compat)
  const [activeTab, setActiveTab] = useState('proposal');
  const [currentPage, setCurrentPage] = useState(1);

  // Unlocked tabs: null or empty from account -> all unlocked; otherwise only those in array
  const unlockedSet = (unlockedTabIds !== null && unlockedTabIds.length > 0)
    ? new Set(unlockedTabIds)
    : null; // null means all unlocked
  const isTabUnlocked = (tabId) => unlockedSet === null || unlockedSet.has(tabId);

  useEffect(() => {
    if (!currentUser) {
      setAccountFreePage(null);
      return;
    }
    const loadAccount = async () => {
      try {
        const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
        const accountId = userDoc.exists() ? userDoc.data().accountId : null;
        if (!accountId) {
          setUnlockedTabIds(null);
          setAccountFreePage(null);
          return;
        }
        const accountData = await getAccountById(accountId);
        setAccountFreePage(accountData?.freePage ?? null);
        const list = accountData?.unlockedProposalTabs;
        setUnlockedTabIds(Array.isArray(list) ? list : null);
      } catch (err) {
        console.error('Error loading proposal page access:', err);
        setUnlockedTabIds(null);
        setAccountFreePage(null);
      }
    };
    loadAccount();
  }, [currentUser]);

  // If current activeTab is locked, switch to first unlocked tab
  useEffect(() => {
    const list = (unlockedTabIds !== null && unlockedTabIds.length > 0) ? unlockedTabIds : null;
    const allUnlocked = list === null;
    const currentUnlocked = allUnlocked || (list && list.includes(activeTab));
    if (!currentUnlocked && list && list.length > 0) {
      setActiveTab(list[0]);
      setCurrentPage(1);
    } else if (!currentUnlocked) {
      setActiveTab(ALL_TAB_IDS[0]);
      setCurrentPage(1);
    }
  }, [unlockedTabIds, activeTab]);
  
  const getTotalPages = () => {
    if (activeTab === 'proposal') return 7;
    if (activeTab === 'contracts') return 6;
    if (activeTab === 'buildProposal') return 1;
    if (activeTab === 'buildContract') return 1;
    return 1;
  };

  const handleNextPage = () => {
    const totalPages = getTotalPages();
    if (currentPage < totalPages) {
      setCurrentPage(prev => prev + 1);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(prev => prev - 1);
    }
  };

  const getPageTitle = (page) => {
    if (activeTab === 'proposal') {
      const proposalTitles = {
        1: "Cover Page",
        2: "Scope of Work",
        3: "Proposed Budget",
        4: "Timeline",
        5: "Next Steps",
        6: "About Us",
        7: "Measure Meeting"
      };
      return proposalTitles[page] || "";
    } else if (activeTab === 'contracts') {
      const contractTitles = {
        1: "Overview",
        2: "Agreement Page 1",
        3: "Agreement Page 2",
        4: "Agreement Page 3",
        5: "Agreement Page 4",
        6: "Signatures"
      };
      return contractTitles[page] || "";
    } else if (activeTab === 'buildProposal') {
      return "Build Proposal";
    } else if (activeTab === 'buildContract') {
      return "Build Contract";
    }
    return "";
  };

  const getPagePDF = (page) => {
    if (activeTab === 'proposal') {
      return `/proposal-example-pg${page}.pdf#toolbar=0&navpanes=0&scrollbar=0`;
    } else if (activeTab === 'contracts') {
      return `/contract-example-pg${page}.pdf#toolbar=0&navpanes=0&scrollbar=0`;
    } else if (activeTab === 'buildProposal') {
      return `/BuildProposalExample.pdf#toolbar=0&navpanes=0&scrollbar=0`;
    } else if (activeTab === 'buildContract') {
      return `/Buildcontractexample.pdf#toolbar=0&navpanes=0&scrollbar=0`;
    }
    return '';
  };

  const renderContent = () => {
    return (
      <div className="space-y-6">
        {/* Page Navigation */}
        {(activeTab === 'proposal' || activeTab === 'contracts') && (
          <div className="flex items-center justify-between mb-6">
            <button
              onClick={handlePrevPage}
              disabled={currentPage === 1}
              className={`flex items-center px-4 py-2 rounded-md ${
                currentPage === 1
                  ? 'text-gray-400 dark:text-gray-500 cursor-not-allowed'
                  : 'text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20'
              }`}
            >
              <ChevronLeft className="w-5 h-5 mr-2" />
              Previous
            </button>
            <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
              Page {currentPage} of {getTotalPages()}
            </span>
            <button
              onClick={handleNextPage}
              disabled={currentPage === getTotalPages()}
              className={`flex items-center px-4 py-2 rounded-md ${
                currentPage === getTotalPages()
                  ? 'text-gray-400 dark:text-gray-500 cursor-not-allowed'
                  : 'text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20'
              }`}
            >
              Next
              <ChevronRight className="w-5 h-5 ml-2" />
            </button>
          </div>
        )}

        {/* Page Content */}
        <div className="rounded-lg p-8">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl font-bold text-gray-800 dark:text-white mb-6 text-center">
              {getPageTitle(currentPage)}
            </h2>
            <div className="space-y-6">
              <div className="space-y-4">
                <div className="w-full h-[800px] border border-gray-300 dark:border-gray-600 rounded-lg overflow-hidden">
                  <iframe
                    src={getPagePDF(currentPage)}
                    className="w-full h-full"
                    title={`PDF Viewer - ${getPageTitle(currentPage)}`}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (accountFreePage === undefined) {
    return (
      <div className="min-h-screen">
        <StartSidebar />
        <div className="min-h-screen bg-gray-50 dark:bg-gray-950 p-6 flex items-center justify-center">
          <BBALoader size={40} />
        </div>
      </div>
    );
  }

  if (accountFreePage !== PROPOSALS_FREE_PAGE) {
    return (
      <div className="min-h-screen">
        <StartSidebar />
        <div className="min-h-screen bg-gray-50 dark:bg-gray-950 p-6">
          <div className="max-w-lg mx-auto text-center pt-16">
            <Lock className="w-14 h-14 mx-auto text-gray-400 dark:text-gray-500 mb-4" aria-hidden />
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Proposals is locked</h1>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              This template is available only when Proposals is your complimentary resource. Your Sales hub shows what you can access.
            </p>
            <BackButton onClick={() => navigate('/start/sales')} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <StartSidebar />
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-between items-center mb-6">
            <BackButton onClick={() => navigate('/start/sales')} />
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Proposal Template</h1>
            <div className="w-10"></div> {/* Spacer to center the title */}
          </div>

          {/* Tabs - all 4 shown; unlocked clickable, locked with lock icon and disabled */}
          <div className="rounded-lg mb-6">
            <div className="border-b border-gray-200 dark:border-gray-700">
              <nav className="-mb-px flex justify-center space-x-8 px-6" aria-label="Tabs">
                {[
                  { id: 'proposal', label: 'Design Proposal', Icon: FileText },
                  { id: 'contracts', label: 'Design Contract', Icon: FileCheck },
                  { id: 'buildProposal', label: 'Build Proposal', Icon: FileText },
                  { id: 'buildContract', label: 'Build Contract', Icon: FileCheck }
                ].map(({ id, label, Icon }) => {
                  const unlocked = isTabUnlocked(id);
                  return unlocked ? (
                    <button
                      key={id}
                      onClick={() => {
                        setActiveTab(id);
                        setCurrentPage(1);
                      }}
                      className={`
                        whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center
                        ${activeTab === id
                          ? 'border-red-500 text-red-600 dark:text-red-400'
                          : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                        }
                      `}
                    >
                      <Icon className="w-5 h-5 mr-2" />
                      {label}
                    </button>
                  ) : (
                    <span
                      key={id}
                      className="whitespace-nowrap py-4 px-1 border-b-2 border-transparent font-medium text-sm flex items-center text-gray-400 dark:text-gray-500 cursor-not-allowed"
                      aria-disabled="true"
                      title="This tab is locked"
                    >
                      <Lock className="w-5 h-5 mr-2 shrink-0" />
                      {label}
                    </span>
                  );
                })}
              </nav>
            </div>
            {/* Download Links */}
            {activeTab !== 'buildProposal' && (
              <div className="flex justify-center py-3">
                <a
                  href={activeTab === 'proposal' 
                    ? "https://www.canva.com/design/DAGuwkJ349Y/ZX-C78dfocQj0ztQfAisyQ/view?utm_content=DAGuwkJ349Y&utm_campaign=designshare&utm_medium=link&utm_source=publishsharelink&mode=preview"
                    : activeTab === 'contracts'
                    ? "/Design-Contract-Template.docx"
                    : activeTab === 'buildContract'
                    ? "/ConstructionContractTemplate.docx"
                    : "#"
                  }
                  target={activeTab === 'proposal' ? "_blank" : undefined}
                  rel={activeTab === 'proposal' ? "noopener noreferrer" : undefined}
                  download={activeTab === 'contracts' ? "Design-Contract-Template.docx" : activeTab === 'buildContract' ? "ConstructionContractTemplate.docx" : undefined}
                  className="inline-flex items-center text-sm text-white bg-red-600 hover:bg-red-700 dark:bg-red-600 dark:hover:bg-red-700 rounded-full px-4 py-2 transition-colors"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  Download the template
                </a>
              </div>
            )}
          </div>
          
          {/* Content */}
          {renderContent()}
        </div>
      </div>
    </div>
  );
};

export default StartProposals;

